import { imageUrl } from '../lib/api'

interface Props {
  emoji?: string
  imageKey?: string | null
  name: string
  size?: 'sm' | 'lg'
  /**
   * When supplied and the item actually has a photo, the thumbnail becomes a
   * button that opens it full size. Emoji-only thumbs stay inert -- there is
   * nothing to enlarge.
   */
  onExpand?: () => void
}

/**
 * An item's picture. Falls back to its emoji, then to the first letter of its
 * name, so a card always reads as something rather than an empty grey box.
 */
export function Thumb({ emoji, imageKey, name, size = 'sm', onExpand }: Props) {
  const className = size === 'lg' ? 'thumb lg' : 'thumb'

  if (!imageKey) {
    return (
      <span className={className} aria-hidden="true">
        {emoji || name.charAt(0).toUpperCase()}
      </span>
    )
  }

  const img = <img src={imageUrl(imageKey)} alt="" loading="lazy" decoding="async" />

  if (!onExpand) return <span className={className}>{img}</span>

  return (
    <button
      type="button"
      className={`${className} thumb-button`}
      /*
       * The card around this is draggable from anywhere, so the thumbnail says
       * it is not: `data-no-drag` is what the sensors look for. Without it a
       * press here picks the card up instead of opening the photo, which is
       * exactly the thing a thumbnail is for.
       */
      data-no-drag
      onClick={(e) => {
        e.stopPropagation()
        onExpand()
      }}
      aria-label={`View photo of ${name}`}
      title={`View photo of ${name}`}
    >
      {img}
    </button>
  )
}
